import {
  FACESHEET_CONDITION_SORT_MODES,
  type FacesheetConditionSortDirection,
  type FacesheetConditionSortMode,
} from '@seen/core/models/conditions/Diagnosis'
import { Button, cn } from '@seen/ui'
import {
  IconCheck,
  IconChevronDown,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react'
import type { JSX } from 'react'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'

const SORT_MODE_LABELS = {
  anatomical: 'Anatomical',
  chronological: 'Chronological',
  alphabetical: 'Alphabetical',
} as const satisfies Record<FacesheetConditionSortMode, string>

function directionToggleLabel(
  mode: FacesheetConditionSortMode,
  direction: FacesheetConditionSortDirection
): string {
  if (mode === 'chronological') {
    return direction === 'desc' ? 'Sort oldest first' : 'Sort newest first'
  }
  return direction === 'asc' ? 'Sort Z to A' : 'Sort A to Z'
}

export interface ConditionsCardSortProps {
  mode: FacesheetConditionSortMode
  onModeChange: (mode: FacesheetConditionSortMode) => void
  direction: FacesheetConditionSortDirection
  onDirectionChange: (direction: FacesheetConditionSortDirection) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ConditionsCardSort({
  mode,
  onModeChange,
  direction,
  onDirectionChange,
  open,
  onOpenChange,
}: ConditionsCardSortProps): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="unstyled"
            size="unstyled"
            className="flex items-center gap-0.5 rounded-sm text-xs text-foreground-secondary focus-visible:ring-2 focus-visible:ring-offset-0"
          >
            <span>
              <span className="font-semibold">Sort by:</span>
              {` ${SORT_MODE_LABELS[mode]}`}
            </span>
            <IconChevronDown aria-hidden className="size-3.5 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-30">
          {FACESHEET_CONDITION_SORT_MODES.map((sortMode) => (
            <DropdownMenuItem
              key={sortMode}
              className={cn(
                'justify-between text-xs',
                sortMode === mode && 'bg-muted'
              )}
              onSelect={() => onModeChange(sortMode)}
            >
              {SORT_MODE_LABELS[sortMode]}
              {sortMode === mode && (
                <IconCheck aria-hidden className="size-4" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {mode !== 'anatomical' && (
        <Button
          variant="unstyled"
          size="unstyled"
          aria-label={directionToggleLabel(mode, direction)}
          className="flex items-center justify-center rounded bg-muted p-0.5 text-foreground-secondary [&_svg]:size-4"
          onClick={() =>
            onDirectionChange(direction === 'desc' ? 'asc' : 'desc')
          }
        >
          {direction === 'desc' ? (
            <IconSortDescending />
          ) : (
            <IconSortAscending />
          )}
        </Button>
      )}
    </div>
  )
}
